import React, { useEffect, useState } from 'react'
import axios from 'axios'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  TextField,
  IconButton,
} from '@mui/material'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import SkeletonLoader from './SkeletonLoader'

const defaultSources = ['SP', 'Copay NBRx', 'HUB']

// const sourceDescriptions = {
//   SP: 'Specialty Pharmacy Stream',
//   'Copay NBRx': 'Financial Assistance Programs',
//   HUB: 'Centralized Hub Records',
// }

const getToday = () => new Date().toISOString().split('T')[0]

const handleIntegerInput = (value) => {
  if (value === "") return "";
  if (/^\d+$/.test(value)) return Number(value);
  return null; // invalid
};

const getCookieMap = () =>
  document.cookie.split('; ').reduce((acc, cookie) => {
    const [key, value] = cookie.split('=')
    if (key) acc[key] = value
    return acc
  }, {})

const toCommaSeparated = (values) => {
  if (!Array.isArray(values)) return ''
  return values
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter(Boolean)
    .join(', ')
}

const blockedNumericKeys = ['-', '+', 'e', 'E', ',']

const isValidNumeric = (value) => /^\d*\.?\d*$/.test(value)

const toNullableNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null

  const normalizedValue = String(value).trim()

  if (!normalizedValue || !/^\d+\.?\d*$/.test(normalizedValue)) return null

  return Number(normalizedValue)
}

const initialRow = (source, date) => ({
  id: Date.now() + Math.random().toString(36).substring(2, 9),
  source,
  max_date: date,
  total_trx: 0,
  total_nbrx: 0,
  record_count: 0,
  new_nbrx: 0,
  added_parents: [],
  removed_nbrx: 0,
  removed_parents: [],
  shifted_nbrx: 0,
  shifted_parents: [],
})

const numberOrEmpty = (rawValue) => (rawValue === '' ? '' : Number(rawValue))
const numberOrZero = (value) => {
  if (value === '' || value === null || value === undefined) return 0
  const numeric = Number(value)
  return Number.isNaN(numeric) ? 0 : numeric
}

const NewEntryTab = () => {
  const today = getToday()
  const [pharmaDetails, setPharmaDetails] = useState({
    pharma_parents: [],
    brands: [],
    sources: [],
  })
  const [selectedBrand, setSelectedBrand] = useState('ADBRY')
  const [entryDate, setEntryDate] = useState(today)
  const [rows, setRows] = useState([])
  const [availableSources, setAvailableSources] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const compactSelectMenuProps = {
    PaperProps: {
      sx: {
        '& .MuiMenuItem-root': {
          minHeight: 32,
          py: 0.25,
          fontSize: '0.875rem',
        },
        '& .MuiCheckbox-root': {
          p: 0.5,
          mr: 0.5,
        },
        '& .MuiListItemText-root': {
          my: 0,
        },
        '& .MuiListItemText-primary': {
          fontSize: '0.875rem',
          lineHeight: 1.2,
        },
      },
    },
  }

  const resetRows = (sources, date) =>
    sources.map((source) => initialRow(source, date))

  useEffect(() => {
    const fetchPharmaDetails = async () => {
      setLoading(true)
      setError('')

      try {
        const response = await axios.get('/pharma-details/')
        const data = response.data?.data || {}
        const parents = data.pharma_parents || []
        const brands = data.brands || []
        const sources = data.sources?.length ? data.sources : defaultSources

        setPharmaDetails({
          pharma_parents: parents,
          brands,
          sources,
        })

        if (!selectedBrand && brands.length) {
          setSelectedBrand(brands[0])
        }

        setRows(resetRows(sources, entryDate))
      } catch (err) {
        console.error('Error fetching pharma details:', err)
        setError('Failed to load pharma parent, brand and source values.')
        setRows(resetRows(defaultSources, entryDate))
      } finally {
        setLoading(false)
      }
    }

    fetchPharmaDetails()
  }, [])

  const fetchAvailableSources = async () => {
    if (!entryDate || !selectedBrand) return
    setLoading(true)
    try {
      const response = await axios.get(`/source-list/?run_date=${entryDate}&brand=${selectedBrand}`)
      const sources = response.data?.data?.sources || []
      setAvailableSources(sources)
      if (sources.length > 0) {
        setRows(sources.map((src) => initialRow(src, entryDate)))
      } else {
        setRows([])
      }
    } catch (err) {
      console.error('Error fetching available sources:', err)
      setAvailableSources([])
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAvailableSources()
  }, [entryDate, selectedBrand])

  const handleBrandClick = (brand) => {
    setSelectedBrand(brand)
  }

  const handleRowChange = (index, field, value) => {
    setRows((prevRows) => {
      const updated = [...prevRows]
      updated[index] = {
        ...updated[index],
        [field]: value,
      }
      return updated
    })
  }

  const handleNumericChange = (index, field, value) => {
    let processedValue = value
    // Strip leading zero if typing another digit (e.g. "05" -> "5"), but allow "0."
    if (value.length > 1 && value.startsWith('0') && value[1] !== '.') {
      processedValue = value.substring(1)
    }

    if (processedValue === '' || isValidNumeric(processedValue)) {
      handleRowChange(index, field, processedValue)
    }
  }

  const handleNumericKeyDown = (event) => {
    if (blockedNumericKeys.includes(event.key)) {
      event.preventDefault()
    }
  }

  const handleNumericFocus = (event) => {
    const target = event.target
    setTimeout(() => {
      target.select()
    }, 0)
  }

  const handleNumericPaste = (event) => {
    const pastedValue = event.clipboardData.getData('text')

    if (!/^\d*\.?\d*$/.test(pastedValue)) {
      event.preventDefault()
    }
  }

  const handleSave = async () => {
    setSaving(true)

    const clearForm = () => {
      setRows([])
      fetchAvailableSources()
    }

    try {
      const validationErrors = []

      if (!entryDate) validationErrors.push('run_date is required (YYYY-MM-DD).')
      if (!selectedBrand || !selectedBrand.trim()) validationErrors.push('brand is required.')
      if (!Array.isArray(rows) || !rows.length) validationErrors.push('At least one entry is required.')

      const sourceValues = (rows || [])
        .map((row) => (row?.source ? String(row.source).trim() : ''))
        .filter(Boolean)

      if (sourceValues.length !== rows.length) {
        validationErrors.push('Each entry must have a non-empty source.')
      }

      const duplicates = [...new Set(sourceValues.filter((value, idx) => sourceValues.indexOf(value) !== idx))]
      if (duplicates.length) {
        validationErrors.push(`Duplicate source values in request: ${duplicates.join(', ')}`)
      }

      if (validationErrors.length) {
        toast.error(validationErrors[0] || 'Validation failed.')
        return
      }

      const payload = {
        run_date: entryDate,
        brand: selectedBrand.trim(),
        entries: rows.map((row) => ({
          source: String(row.source).trim(),
          max_date: row.max_date || '',
          total_trx: numberOrZero(row.total_trx),
          total_nbrx: numberOrZero(row.total_nbrx),
          record_count: numberOrZero(row.record_count),
          newly_added_nbrx: numberOrZero(row.new_nbrx),
          added_pharmparents: toCommaSeparated(row.added_parents),
          removed_nbrx: numberOrZero(row.removed_nbrx),
          removed_pharmparents: toCommaSeparated(row.removed_parents),
          shifts_nbrx: numberOrZero(row.shifted_nbrx),
          shifted_pharmparents: toCommaSeparated(row.shifted_parents),
        })),
      }

      const cookies = getCookieMap()
      const headers = {
        'Content-Type': 'application/json',
        ...(cookies.csrftoken ? { 'X-CSRFToken': cookies.csrftoken } : {}),
      }

      await axios.post('/pharma-tracker/', payload, { headers })
      toast.success('Entry saved successfully.')
      clearForm()
    } catch (err) {
  console.error(err)
 const detials = err?.response?.data || {}
    const serverError =
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    'Failed to save entry. Please try again.'

  toast.error(serverError)
}  finally {
      setSaving(false)
    }
  }

  return (
    <Box sx={{ display: 'grid', width: '100%' }}>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: '12px',
          bgcolor: 'background.paper',
          boxShadow: 'none',
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 165px)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#101828',fontSize:"1rem" }}>
              New Entry
            </Typography>
            {/* <Typography variant="body2" sx={{ color: '#475569', mt: 0.5 }}>
              Selection Date
            </Typography> */}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 2,
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <TextField
              type="date"
              size="small"
              value={entryDate}
              onChange={(event) => setEntryDate(event.target.value)}
              inputProps={{ max: today }}
              sx={{
                minWidth: 180,
                bgcolor: '#F8FAFC',
                borderRadius: '12px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#E2E8F0',
                },
              }}
            />

            <Box 
              sx={{ 
                display: 'inline-flex', 
                alignItems: 'center',
                gap: '2px',
                p: '3px',
                background: '#f4f6fb',
                borderRadius: '12px',
                border: '1px solid #eef1f6',
                minHeight: '38px',
                flexWrap: 'wrap'
              }}
            >
              {(pharmaDetails.brands.length ? pharmaDetails.brands : ['ADBRY', 'ANZUPGO']).map((brand) => {
                const isSelected = selectedBrand === brand;
                return (
                  <Button
                    key={brand}
                    variant="text"
                    onClick={() => handleBrandClick(brand)}
                    sx={{
                      minWidth: '90px',
                      height: '30px',
                      px: '14px',
                      borderRadius: '8px',
                      color: isSelected ? '#0576FC' : '#7b8798',
                      background: isSelected ? '#fff' : 'transparent',
                      boxShadow: isSelected ? '0px 1px 4px rgba(15, 23, 42, 0.08)' : 'none',
                      fontSize: '12px',
                      fontWeight: 700,
                      textTransform: 'none',
                      '&:hover': {
                        background: isSelected ? '#fff' : 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    {brand}
                  </Button>
                );
              })}
            </Box>
          </Box>
        </Box>

 
      {/* Old loader removed */}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            mb: 2,
            pr: 1,
            marginX: '1%',
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(148, 163, 184, 0.45)',
              borderRadius: '999px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(241, 245, 249, 0.9)',
              borderRadius: '999px',
            },
          }}
        >
          {loading ? (
            <SkeletonLoader type="pharmaNewEntry" cardCount={3} />
          ) : rows.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                minHeight: '280px', // Reduced to prevent overflow
                bgcolor: '#F8FAFC',
                borderRadius: '16px',
                border: '1px dashed #CBD5E1',
                textAlign: 'center',
                p: 4,
                gap: 2,
              }}
            >
              <Box
                sx={{ 
                  width: '240px', 
                  height: '180px', 
                  mb: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                {/* Advanced 3D Isometric Success Scene - Code Based */}
                <svg width="220" height="160" viewBox="0 0 220 160" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Decorative Background Elements */}
                  <rect x="40" y="80" width="140" height="40" rx="8" fill="#F1F5F9" transform="rotate(-15 40 80)"/>
                  <rect x="50" y="60" width="140" height="40" rx="8" fill="#E2E8F0" transform="rotate(-15 50 60)"/>
                  
                  {/* Main 3D Card */}
                  <g filter="url(#scene_shadow)">
                    <rect x="60" y="40" width="140" height="90" rx="12" fill="white"/>
                    <rect x="75" y="60" width="110" height="8" rx="4" fill="#F1F5F9"/>
                    <rect x="75" y="75" width="80" height="8" rx="4" fill="#F1F5F9"/>
                    <rect x="75" y="90" width="100" height="30" rx="6" fill="#EFF6FF"/>
                  </g>

                  {/* Floating 3D Success Badge */}
                  <g filter="url(#badge_shadow)">
                    <circle cx="160" cy="50" r="35" fill="url(#badge_grad)"/>
                    <circle cx="160" cy="50" r="28" fill="white" fillOpacity="0.2"/>
                    <path d="M148 50L156 58L172 42" stroke="white" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
                  </g>

                  <defs>
                    <linearGradient id="badge_grad" x1="125" y1="15" x2="195" y2="85" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#0B69FF"/>
                      <stop offset="1" stopColor="#0356D9"/>
                    </linearGradient>
                    <filter id="scene_shadow" x="30" y="20" width="190" height="140" filterUnits="userSpaceOnUse">
                      <feDropShadow dx="0" dy="15" stdDeviation="15" floodColor="#0F172A" floodOpacity="0.08"/>
                    </filter>
                    <filter id="badge_shadow" x="115" y="15" width="90" height="90" filterUnits="userSpaceOnUse">
                      <feDropShadow dx="0" dy="10" stdDeviation="10" floodColor="#0B69FF" floodOpacity="0.25"/>
                    </filter>
                  </defs>
                </svg>
              </Box>
              <Typography sx={{ color: '#1E293B', fontSize: '20px', fontWeight: 700 }}>
                All Entries Recorded
              </Typography>
              <Typography sx={{ color: '#64748B', fontSize: '15px', fontWeight: 500, maxWidth: '450px', lineHeight: 1.6 }}>
                All source entries for <b>{selectedBrand}</b> for this date (<b>{entryDate}</b>) have already been recorded. No further entries are required for this selection.
              </Typography>
            </Box>
          ) : (
            rows.map((row, index) => (
            <Paper
              key={`${row.source}-${index}`}
              elevation={0}
              sx={{
                borderRadius: '12px',
                p: 2,
                bgcolor: 'background.paper',
                border: '1px solid rgba(15, 23, 42, 0.08)',
                boxShadow: 'none',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'flex-start', md: 'center' },
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 1.5,
                }}
              >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      bgcolor: '#FFF7ED', // Orange bg
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '6px',
                      borderLeft: '4px solid #EA580C', // Rich orange accent
                      mb: 1.5,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: '#EA580C', // Orange text
                        fontWeight: 700,
                        fontSize: '13px',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                      }}
                    >
                      Source : {row.source}
                    </Typography>
                  </Box>
              </Box>

              <Grid container spacing={1}>
                <Grid item xs={12} sm={6} md={2.4}>
                  <TextField
                    label="Max Date"
                    type="date"
                    size="small"
                    value={row.max_date}
                    onChange={(event) => handleRowChange(index, 'max_date', event.target.value)}
                    inputProps={{ max: today }}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={{
                      bgcolor: '#fff',
                      borderRadius: '12px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E2E8F0',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <TextField
                    label="Total TRX"
                    type="number"
                    size="small"
                    value={row.total_trx}
                    onChange={(event) => handleNumericChange(index, 'total_trx', event.target.value)}
                    onKeyDown={handleNumericKeyDown}
                    onFocus={handleNumericFocus}
                    inputProps={{ inputMode: 'decimal', step: 'any', min: 0 }}
                    fullWidth
                    sx={{
                      bgcolor: '#fff',
                      borderRadius: '12px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E2E8F0',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <TextField
                    label="Total NBRX"
                    type="number"
                    size="small"
                    value={row.total_nbrx}
                    onChange={(event) => handleNumericChange(index, 'total_nbrx', event.target.value)}
                    onKeyDown={handleNumericKeyDown}
                    onFocus={handleNumericFocus}
                    inputProps={{ inputMode: 'decimal', step: 'any', min: 0 }}
                    fullWidth
                    sx={{
                      bgcolor: '#fff',
                      borderRadius: '12px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E2E8F0',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <TextField
                    label="Record Count"
                    type="number"
                    size="small"
                    value={row.record_count}
                    onChange={(event) => handleNumericChange(index, 'record_count', event.target.value)}
                    onKeyDown={handleNumericKeyDown}
                    onFocus={handleNumericFocus}
                    inputProps={{ inputMode: 'decimal', step: 'any', min: 0 }}
                    fullWidth
                    sx={{
                      bgcolor: '#fff',
                      borderRadius: '12px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E2E8F0',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <TextField
                    label="New NBRX"
                    type="number"
                    size="small"
                    value={row.new_nbrx}
                    onChange={(event) => handleNumericChange(index, 'new_nbrx', event.target.value)}
                    onKeyDown={handleNumericKeyDown}
                    onFocus={handleNumericFocus}
                    inputProps={{ inputMode: 'decimal', step: 'any', min: 0 }}
                    fullWidth
                    sx={{
                      bgcolor: '#fff',
                      borderRadius: '12px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E2E8F0',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <TextField
                    select
                    label="Added Parents"
                    size="small"
                    value={row.added_parents}
                    onChange={(event) => handleRowChange(index, 'added_parents', event.target.value)}
                    SelectProps={{
  multiple: true,
  renderValue: (selected) => (selected.length ? selected.join(', ') : 'Select Parent...'),
  MenuProps: {
    PaperProps: {
      sx: {
        maxHeight: 220,
        '& .MuiMenuItem-root': {
          minHeight: 32,
          paddingTop: '4px',
          paddingBottom: '4px',
        },
        '& .MuiCheckbox-root': {
          padding: '4px',
        },
        '& .MuiListItemText-root .MuiTypography-root': {
          fontSize: '13px',
          lineHeight: 1.2,
        },
      },
    },
  },
}}
                    fullWidth
                    sx={{
                      bgcolor: '#fff',
                      borderRadius: '12px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E2E8F0',
                      },
                    }}
                  >
                    {pharmaDetails.pharma_parents.map((parent) => (
                      <MenuItem key={parent} value={parent}>
                        <Checkbox checked={row.added_parents.indexOf(parent) > -1} />
                        <ListItemText primary={parent} />
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <TextField
                    label="Removed NBRX"
                    type="number"
                    size="small"
                    value={row.removed_nbrx}
                    onChange={(event) => handleNumericChange(index, 'removed_nbrx', event.target.value)}
                    onKeyDown={handleNumericKeyDown}
                    onFocus={handleNumericFocus}
                    inputProps={{ inputMode: 'decimal', step: 'any', min: 0 }}
                    fullWidth
                    sx={{
                      bgcolor: '#fff',
                      borderRadius: '12px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E2E8F0',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <TextField
                    select
                    label="Removed Parents"
                    size="small"
                    value={row.removed_parents}
                    onChange={(event) => handleRowChange(index, 'removed_parents', event.target.value)}
                    SelectProps={{
  multiple: true,
  renderValue: (selected) => (selected.length ? selected.join(', ') : 'Select Parent...'),
  MenuProps: {
    PaperProps: {
      sx: {
        maxHeight: 220,
        '& .MuiMenuItem-root': {
          minHeight: 32,
          paddingTop: '4px',
          paddingBottom: '4px',
        },
        '& .MuiCheckbox-root': {
          padding: '4px',
        },
        '& .MuiListItemText-root .MuiTypography-root': {
          fontSize: '13px',
          lineHeight: 1.2,
        },
      },
    },
  },
}}
                    fullWidth
                    sx={{
                      bgcolor: '#fff',
                      borderRadius: '12px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E2E8F0',
                      },
                    }}
                  >
                    {pharmaDetails.pharma_parents.map((parent) => (
                      <MenuItem key={parent} value={parent}>
                        <Checkbox checked={row.removed_parents.indexOf(parent) > -1} />
                        <ListItemText primary={parent} />
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <TextField
                    label="Shifted NBRX"
                    type="number"
                    size="small"
                    value={row.shifted_nbrx}
                    onChange={(event) => handleNumericChange(index, 'shifted_nbrx', event.target.value)}
                    onKeyDown={handleNumericKeyDown}
                    onFocus={handleNumericFocus}
                    inputProps={{ inputMode: 'decimal', step: 'any', min: 0 }}
                    fullWidth
                    sx={{
                      bgcolor: '#fff',
                      borderRadius: '12px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E2E8F0',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <TextField
                    select
                    label="Shifted Parents"
                    size="small"
                    value={row.shifted_parents}
                    onChange={(event) => handleRowChange(index, 'shifted_parents', event.target.value)}
                    SelectProps={{
  multiple: true,
  renderValue: (selected) => (selected.length ? selected.join(', ') : 'Select Parent...'),
  MenuProps: {
    PaperProps: {
      sx: {
        maxHeight: 220,
        '& .MuiMenuItem-root': {
          minHeight: 32,
          paddingTop: '4px',
          paddingBottom: '4px',
        },
        '& .MuiCheckbox-root': {
          padding: '4px',
        },
        '& .MuiListItemText-root .MuiTypography-root': {
          fontSize: '13px',
          lineHeight: 1.2,
        },
      },
    },
  },
}}
                    fullWidth
                    sx={{
                      bgcolor: '#fff',
                      borderRadius: '12px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E2E8F0',
                      },
                    }}
                  >
                    {pharmaDetails.pharma_parents.map((parent) => (
                      <MenuItem key={parent} value={parent}>
                        <Checkbox checked={row.shifted_parents.indexOf(parent) > -1} />
                        <ListItemText primary={parent} />
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Paper>
          )))}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'end',
            alignItems: 'center',
            gap: 2,
            mt: 'auto',
            pt: 2,
            borderTop: '1px solid rgba(15, 23, 42, 0.08)',
            bgcolor: 'background.paper',
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
          }}
        >
          {/* <Button
            variant="text"
            onClick={handleDiscard}
            sx={{
              textTransform: 'none',
              color: '#475569',
              '&:hover': { bgcolor: '#F8FAFC' },
            }}
          >
            Discard Draft
          </Button> */}
{!loading && (
            <Button
              variant="contained"
              size="large"
              onClick={handleSave}
              disabled={loading || !rows.length}
              sx={{
                px: 4,
                py: 1.25,
                borderRadius: '999px',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '14px',
                bgcolor: '#0576FC',
                boxShadow: '0 4px 14px 0 rgba(5, 118, 252, 0.39)',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  bgcolor: '#0466db',
                  boxShadow: '0 6px 20px rgba(5, 118, 252, 0.23)',
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
                '&.Mui-disabled': {
                  bgcolor: '#E2E8F0',
                  color: '#94A3B8',
                }
              }}
            >
               Save {selectedBrand}
            </Button>
)}
        </Box>

        <ToastContainer
          position="top-right"
          autoClose={3500}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnHover
          theme="light"
        />
      </Paper>
    </Box>
  )
}

export default NewEntryTab
